import { useEffect, useRef, useState } from "react";
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { SelectButton } from 'primereact/selectbutton';
import { FilterMatchMode } from 'primereact/api';
import { Toast } from 'primereact/toast';
import ReportService from '../../services/ReportService';

const currency = new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' });
const fmtMoney = (v) => currency.format(v ?? 0);

export default function CustomerBalanceReportPage() {
    const [mounted, setMounted] = useState(false);
    useEffect(() => { setMounted(true); }, []);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [balanceFilter, setBalanceFilter] = useState('ALL');
    const [globalFilterValue, setGlobalFilterValue] = useState('');
    const [filters, setFilters] = useState({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    });
    const toast = useRef(null);
    const dt = useRef(null);

    const fetchReport = async () => {
        setLoading(true);
        try {
            const res = await ReportService.getCustomerBalanceReport();
            setData(res.data.data);
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Hata', detail: 'Rapor alınamadı' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReport();
    }, []);

    const onGlobalFilterChange = (e) => {
        const value = e.target.value;
        let _filters = { ...filters };
        _filters['global'].value = value;
        setFilters(_filters);
        setGlobalFilterValue(value);
    };

    const exportCSV = () => {
        dt.current.exportCSV();
    };

    const totalReceivable = data.reduce((acc, r) => acc + (r.balance || 0), 0);
    const debtorCount = data.filter(r => r.balance > 0).length;

    const filterOptions = [
        { label: 'HEPSİ', value: 'ALL' },
        { label: 'BORÇLULAR', value: 'DEBTORS' },
        { label: 'BAKİYESİ 0 OLANLAR', value: 'ZERO' }
    ];

    const filteredData = data.filter(r => {
        if (balanceFilter === 'DEBTORS') return r.balance > 0.001;
        if (balanceFilter === 'ZERO') return Math.abs(r.balance) <= 0.001;
        return true;
    });

    const header = (
        <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4 py-4 px-2">
            <div className="flex items-center gap-4 w-full xl:w-auto">
                 <Button icon="pi pi-refresh" onClick={fetchReport} loading={loading} className="p-button-rounded p-button-text text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-all" />
                 <div className="flex flex-col">
                    <h2 className="text-lg font-black text-slate-800 tracking-tight">Cari Bakiye Listesi</h2>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{filteredData.length} Müşteri Listeleniyor</p>
                 </div>
            </div>
            <div className="flex flex-wrap items-center gap-4 w-full xl:w-auto mt-4 xl:mt-0">
                <div className="bg-slate-100 p-1 rounded-2xl flex items-center gap-1">
                    {filterOptions.map(opt => (
                        <button
                            key={opt.value}
                            onClick={() => setBalanceFilter(opt.value)}
                            className={`px-4 py-2 rounded-xl text-[11px] font-black transition-all ${
                                balanceFilter === opt.value
                                ? 'bg-white text-blue-600 shadow-sm'
                                : 'text-slate-500 hover:text-slate-700'
                            }`}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
                <span className="p-input-icon-left flex-1 xl:flex-none">
                    <i className="pi pi-search text-slate-400 pl-2" />
                    <InputText value={globalFilterValue} onChange={onGlobalFilterChange} placeholder="Müşteri veya telefon ara..." className="w-full p-5 !py-2 bg-slate-50 border-slate-200 rounded-xl font-bold text-sm h-[40px]" />
                </span>
                <Button type="button" icon="pi pi-file-excel" label="EXCEL" severity="success" onClick={exportCSV} className="!bg-emerald-600 border-none rounded-xl px-4 h-[40px] font-black text-[11px] text-white" />
            </div>
        </div>
    );

    return (
        <div className="p-2 md:p-4  bg-[#f8fafc] min-h-screen animate-fade-in">
            <Toast ref={toast}  appendTo={mounted ? document.body : "self"} />

            {/* Elite Header */}
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                <div className="flex items-center gap-6">
                    <div className="w-20 h-20 rounded-[2rem] !bg-blue-900 flex items-center justify-center shadow-2xl !shadow-blue-200">
                        <i className="pi pi-users text-white text-3xl" />
                    </div>
                    <div>
                        <h1 className="text-3xl md:text-4xl font-black text-slate-900 leading-tight tracking-tighter uppercase">Müşteri Bakiyeleri</h1>
                        <div className="flex items-center gap-3 mt-1">
                            <span className="w-2 h-2 rounded-full !bg-blue-500 animate-pulse" />
                            <p className="text-slate-500 font-bold text-sm uppercase tracking-widest italic">Cari Borç ve Alacak Takibi</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-4">
                <div className="!bg-blue-200 p-4 rounded-[2.5rem] border border-white/50 shadow-xl shadow-blue-500/10 flex items-center justify-between transition-all hover:scale-[1.02]">
                    <div>
                        <p className="text-[14px] font-black !text-blue-600 uppercase tracking-[0.2em] mb-2">Toplam Alacak</p>
                        <h3 className="text-3xl font-black text-blue-600 tracking-tighter">{fmtMoney(totalReceivable)}</h3>
                    </div>
                    <div className="w-14 h-14 rounded-2xl bg-white shadow-sm flex items-center justify-center !text-blue-600">
                        <i className="pi pi-money-bill text-2xl" />
                    </div>
                </div>
                <div className="!bg-rose-200 p-4 rounded-[2.5rem] border border-white/50 shadow-xl shadow-rose-500/10 flex items-center justify-between transition-all hover:scale-[1.02]">
                    <div>
                        <p className="text-[14px] font-black text-rose-600 uppercase tracking-[0.2em] mb-2">Borçlu Müşteri Sayısı</p>
                        <h3 className="text-3xl font-black text-rose-600 tracking-tighter">{debtorCount} Kişi</h3>
                    </div>
                    <div className="w-14 h-14 rounded-2xl bg-white shadow-sm flex items-center justify-center text-rose-600">
                        <i className="pi pi-user-minus text-2xl" />
                    </div>
                </div>
            </div>

            <div className="bg-white shadow-2xl shadow-slate-200/50 border border-slate-200 rounded-[2.5rem] overflow-hidden">
                <div className=" p-2 md:p-4">
                    <DataTable
                        ref={dt}
                        value={filteredData}
                        loading={loading}
                        filters={filters}
                        globalFilterFields={['customerName', 'phone']}
                        header={header}
                        paginator
                        rows={15}
                        rowsPerPageOptions={[15, 30, 50]}
                        className="p-datatable-sm"
                        emptyMessage="Kayıt bulunamadı."
                        removableSort
                        rowClassName={() => "hover:bg-slate-50/80 transition-colors"}
                    >
                        <Column field="customerName" header="MÜŞTERİ" body={(r) => (
                             <div className="flex flex-col">
                                <a href={`/customers/detail/${r.id}`} target="_blank" rel="noreferrer" className="font-black text-blue-600 hover:text-blue-800 transition-colors cursor-pointer">
                                    {r.customerName}
                                </a>
                                <span className="text-[10px] text-slate-400 font-bold uppercase tracking-tighter">{r.phone || 'Telefon Belirtilmedi'}</span>
                             </div>
                        )} sortable headerClassName="bg-slate-50 text-slate-500 font-black text-[11px] uppercase py-4 px-6" className="py-4 px-6" />

                        <Column field="openingBalance" header="AÇILIŞ BAKİYESİ" body={(r) => <span className="font-bold text-slate-400">{fmtMoney(r.openingBalance)}</span>} sortable headerClassName="bg-slate-50 text-slate-500 font-black text-[11px] uppercase py-4 px-6 text-right" className="py-4 px-6" style={{ textAlign: 'right' }} />

                        <Column field="totalSale" header="TOPLAM SATIŞ" body={(r) => <span className="font-bold text-slate-600">{fmtMoney(r.totalSale)}</span>} sortable headerClassName="bg-slate-50 text-slate-500 font-black text-[11px] uppercase py-4 px-6 text-right" className="py-4 px-6" style={{ textAlign: 'right' }} />

                        <Column field="totalPaid" header="TOPLAM TAHSİLAT" body={(r) => <span className="font-black text-emerald-600 bg-emerald-50 px-3 py-1 rounded-lg">{fmtMoney(r.totalPaid)}</span>} sortable headerClassName="bg-slate-50 text-slate-500 font-black text-[11px] uppercase py-4 px-6 text-right" className="py-4 px-6" style={{ textAlign: 'right' }} />

                        <Column field="balance" header="GÜNCEL BAKİYE" body={(r) => (
                            <span className={`font-black ${r.balance > 0 ? 'text-rose-500 bg-rose-50' : 'text-slate-400 bg-slate-50'} px-4 py-1.5 rounded-xl`}>
                                {fmtMoney(r.balance)}
                            </span>
                        )} sortable headerClassName="bg-slate-50 text-slate-500 font-black text-[11px] uppercase py-4 px-6 text-right" className="py-4 px-6" style={{ textAlign: 'right' }} />

                        <Column field="lastPaymentAmount" header="SON TAHSİLAT" body={(r) => (
                            <div className="flex flex-col items-end">
                                <span className="font-bold text-emerald-600">{r.lastPaymentAmount ? fmtMoney(r.lastPaymentAmount) : '—'}</span>
                                <span className="text-[10px] text-slate-400 font-bold">{r.lastPaymentDate ? new Date(r.lastPaymentDate).toLocaleDateString('tr-TR') : ''}</span>
                            </div>
                        )} sortable headerClassName="bg-slate-50 text-slate-500 font-black text-[11px] uppercase py-4 px-6 text-right" className="py-4 px-6" style={{ textAlign: 'right' }} />

                        <Column field="lastTransactionDate" header="SON HAREKET" body={(r) => (
                            <span className="text-[11px] font-bold text-slate-500">
                                {r.lastTransactionDate ? new Date(r.lastTransactionDate).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                            </span>
                        )} sortable headerClassName="bg-slate-50 text-slate-500 font-black text-[11px] uppercase py-4 px-6 text-right" className="py-4 px-6" style={{ textAlign: 'right' }} />
                    </DataTable>
                </div>
            </div>
        </div>
    );
}
